import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Camera, CameraBrands } from '../../../../cameras/camera.model';
import { EdgeService } from '../../../../edge/edge.service';
import { SocketMainService } from '../../../../socket/socket-main.service';
import { SocketEvents } from '../../../../socket/socket.model';
import { combineLatest, map, Observable, Subject, takeUntil } from 'rxjs';
import { LocationModel } from '../../../../locations/location.model';
import { PreloaderColor } from '@enums/shared.enum';
import { ThumbnailsService } from 'src/app/development/thumbnails.service';
import { StorageBucket } from '@consts/storage-url.util';
import * as uuid from 'uuid';
import { TranslateService } from '@ngx-translate/core';
import { select, Store } from '@ngrx/store';
import { EdgeStatusSelectors } from '@states/edge-status/edge-status.selector-types';
import { Edge } from '../../../../edge/edge.model';
import * as EdgeSelectors from '../../../../store/states/edge/edge.selectors';
import * as OrganizationSelectors from '../../../../store/states/organization/organization.selectors';
import * as IntegrationsSelectors from '../../../../store/states/integrations/integrations.selectors';
import { IntegrationsModel } from '@models/integrations.model';
import { IntegrationsService } from '../../../../development/integrations.service';
import { AddCameraWizardModels } from '../../models/wizard.models';
import { WizardCameraCsvUtil } from '../../utils/wizard-camera-csv.util';
import { api } from '@consts/url.const';

export interface CameraAuthState {
    camera: Camera;
    connectionDetails?: LocationModel.CameraConnectionDetails;
    status: 'pending' | 'loading' | 'success' | 'error';
    errorMessage?: string;
}

export interface WizardAuthenticationSnapshot {
    // Basic UI state
    isConnecting: boolean;
    isConnected: boolean;
    progress: number;
    showCredentials: boolean;
    nvrErrorBanner: string | null;

    // Credentials + selection/results
    credentialsArr: LocationModel.Credentials[];
    cameraStatesMap: Array<[string, CameraAuthState]>;
    selectedCameraIds: string[];

    // Manual discovery UI (top table)
    manualDiscoveryProgress: number;
    manualDiscoveryRows: any[];
}

@Component({
    selector: 'app-wizard-authentication',
    templateUrl: './wizard-authentication.component.html',
    styleUrls: ['./wizard-authentication.component.scss'],
    standalone: false,
})
export class WizardAuthenticationComponent implements OnInit, OnDestroy {

    public loaderColor = PreloaderColor;

    @Input() edgeId: string;
    @Input() locationId: string;
    @Input() selectedCameras: Camera[] = [];
    @Input() manualDiscoveryEnabled = false;
    @Input() manualDeviceType: 'camera' | 'nvr' = 'camera';
    @Input() isOnvifFlow = false;
    @Input() csvImportRows: AddCameraWizardModels.ManualCsvImportRow[] = [];
    @Input() snapshot: WizardAuthenticationSnapshot | null = null;

    @Output() cameraDetailsReceived = new EventEmitter<Map<string, CameraAuthState>>();
    @Output() selectedCamerasChanged = new EventEmitter<Set<string>>();
    @Output() back = new EventEmitter<void>();
    @Output() maxSelectableCamerasChange = new EventEmitter<number>();

    private destroy$ = new Subject<void>();

    // Snapshot retry (S3/GCS eventual consistency)
    private readonly snapshotRetryDelayMs = 2500;
    private readonly snapshotMaxAttempts = 3;
    private snapshotAttemptByKey = new Map<string, number>();
    private snapshotBustByKey = new Map<string, number>();
    private snapshotTimerByKey = new Map<string, ReturnType<typeof setTimeout>>();

    // State
    public isConnecting: boolean = false;
    public isConnected: boolean = false;
    public progress: number = 0;
    public showCredentials: boolean = true;

    // Manual discovery (device-info) state
    public manualDiscoveryProgress: number = 0;
    public manualDiscoveryRows: any[] = [];
    private manualDiscoverySessionToken: string | null = null;
    public manualDiscoveryDisplayedColumns: string[] = [
        'ipAddress',
        'mac',
        'minPing',
        'maxPing',
        'avgPing',
        'stdPing',
        'connection',
        'cameraBrand'
    ];

    // Session token to filter notifications from current auth request only
    private currentSessionToken: string | null = null;

    // Camera state map - key is camera id
    public cameraStatesMap: Map<string, CameraAuthState> = new Map();

    // Selected cameras to proceed to next stage
    public selectedCameraIds: Set<string> = new Set();

    // Edge platform -> selection limit
    public Platforms = Edge.V2.Platforms;
    public maxSelectableCameras = 10; // default for non-U1 (will be recomputed), -1 means no limit
    public selectEdgeStatus$!: Observable<Edge.V2.EdgePulsationDisplay>;
    public selectEdgeCamerasNumber$!: Observable<number>;
    public selectDisableCameraLimit$!: Observable<boolean>;

    // Credentials form
    public credentialsArr: LocationModel.Credentials[] = [];

    // NVR UI message
    public nvrErrorBanner: string | null = null;

    // Protocol options
    public protocolOptions = [
        { value: LocationModel.RtspProtocol.TCP, label: 'TCP' },
        { value: LocationModel.RtspProtocol.UDP, label: 'UDP' },
        { value: LocationModel.RtspProtocol.SECURED, label: 'RTSPS' }
    ];

    public static readonly GENETEC_VENDOR = 'genetec';
    public static readonly GENETEC_MEDIA_GATEWAY_PORT = 654;

    public static buildGenetecStreamPath(genetecGuid: string): string {
        return `/${genetecGuid}/live`;
    }

    public static buildGenetecSubStreamPath(genetecGuid: string): string {
        return `/${genetecGuid}/lowres`;
    }

    public nvrVendorOptions: Array<{ value: string; label: string }> = [
        { value: 'hikvision', label: 'Hikvision' },
        { value: 'uniview', label: 'Uniview' },
        { value: 'dahua', label: 'Dahua' },
        { value: 'provision', label: 'Provision' },
    ];

    public isGenetecInstalled = false;
    public genetecConfig: IntegrationsModel.Genetec.Config | null = null;

    public nvrChannelCountOptions: number[] = [4, 8, 16, 32, 64, 128, 256];

    public get showManualDiscoveryTable(): boolean {
        return this.manualDiscoveryEnabled && !this.csvImportRows?.length;
    }

    constructor(
        private edgeService: EdgeService,
        private socketMainService: SocketMainService,
        private translateService: TranslateService,
        private store$: Store,
        private thumbnailsService: ThumbnailsService,
        private integrationsService: IntegrationsService,
        private changeDetectorRef: ChangeDetectorRef,
    ) { }

    ngOnInit(): void {
        this.selectEdgeStatus$ = this.store$.pipe(select(EdgeStatusSelectors.selectEdgesStatusEntities)).pipe(
            map((entities: any) => entities?.[this.edgeId])
        );
        this.selectEdgeCamerasNumber$ = this.store$.pipe(select(EdgeSelectors.selectEdgeCamerasNumberById(this.edgeId)));
        this.selectDisableCameraLimit$ = this.store$.pipe(select(OrganizationSelectors.selectDisableCameraLimit));

        // Re-bind snapshot URLs once the private-storage edge map is ready (avoids first paint on Go proxy).
        this.thumbnailsService.privateStorageMapReady$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                // Defer past the current CD turn (BehaviorSubject may emit synchronously in ngOnInit).
                setTimeout(() => this.changeDetectorRef.detectChanges());
            });

        this.store$.pipe(select(IntegrationsSelectors.isInstalled(IntegrationsModel.Providers.Genetec)), takeUntil(this.destroy$))
            .subscribe((installed) => {
                this.isGenetecInstalled = !!installed;
                const hasGenetecOption = this.nvrVendorOptions.some(o => o.value === WizardAuthenticationComponent.GENETEC_VENDOR);
                if (this.isGenetecInstalled && !hasGenetecOption) {
                    this.nvrVendorOptions = [...this.nvrVendorOptions, { value: WizardAuthenticationComponent.GENETEC_VENDOR, label: 'Genetec' }];
                } else if (!this.isGenetecInstalled && hasGenetecOption) {
                    this.nvrVendorOptions = this.nvrVendorOptions.filter(o => o.value !== WizardAuthenticationComponent.GENETEC_VENDOR);
                }
                if (this.isGenetecInstalled && !this.genetecConfig) {
                    this.loadGenetecConfig();
                }
            });

        // Restore previous state (when navigating back from Configuration -> Authentication)
        if (this.snapshot) {
            this.restoreSnapshot(this.snapshot);
        } else if (this.csvImportRows?.length) {
            this.credentialsArr = WizardCameraCsvUtil.buildCredentialsFromCsvRows(this.csvImportRows);
            this.initializeCameraStates();
        } else {
            this.addCredentialRow();
            this.initializeCameraStates();
            this.applyDefaultBrandPathsToCredentials();
        }

        if (!this.snapshot && this.manualDiscoveryEnabled && this.selectedCameras?.length && !this.csvImportRows?.length) {
            this.startManualDiscovery();
        }

        // Determine selection limit by platform and currently-added cameras on edge
        combineLatest([this.selectEdgeStatus$, this.selectEdgeCamerasNumber$, this.selectDisableCameraLimit$])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([edgeStatus, currentNum, disableCameraLimit]) => {
                if (disableCameraLimit) {
                    // No limit when disableCameraLimit is true
                    this.maxSelectableCameras = 64;
                } else {
                    const platform = (edgeStatus?.metadata as any)?.edgeStatus?.gPlatform;
                    const baseLimit = platform === Edge.V2.Platforms.U1 ? 64 : 10;
                    const remaining = baseLimit - Number(currentNum ?? 0);
                    this.maxSelectableCameras = Math.max(0, remaining);
                }
                this.maxSelectableCamerasChange.emit(this.maxSelectableCameras);

                // Trim selection if current exceeds the limit (only when limit is enabled)
                this.enforceSelectionLimit();
            });
    }

    ngOnDestroy(): void {
        // Cleanup any scheduled snapshot retries
        this.snapshotTimerByKey.forEach((t) => clearTimeout(t));
        this.snapshotTimerByKey.clear();
        this.destroy$.next();
        this.destroy$.complete();
    }

    private initializeCameraStates(): void {
        this.cameraStatesMap.clear();
        this.selectedCameraIds.clear();
        this.selectedCameras.forEach(camera => {
            // Clone the camera object to make it mutable (original may be frozen/immutable)
            const mutableCamera = { ...camera };
            this.cameraStatesMap.set(camera.id, {
                camera: mutableCamera,
                status: 'pending'
            });
        });
    }

    private startManualDiscovery(): void {
        this.manualDiscoveryProgress = 0;
        this.manualDiscoveryRows = [];
        this.manualDiscoverySessionToken = null;

        // Subscribe before triggering request
        this.subscribeToManualDiscoveryUpdates();

        const ipArr = (this.selectedCameras || []).map(c => c.ipAddress).filter(Boolean);
        this.edgeService.manualDiscovery({
            edgeId: this.edgeId,
            deviceType: this.manualDeviceType,
            data: { ipArr }
        }).subscribe({
            next: (res: any) => {
                if (res?.messageId) {
                    this.manualDiscoverySessionToken = res.messageId;
                } else if (res?.token?.session) {
                    this.manualDiscoverySessionToken = res.token.session;
                }
            },
            error: (err) => {
                console.error('Manual discovery failed to start:', err);
            }
        });
    }

    private subscribeToManualDiscoveryUpdates(): void {
        this.socketMainService
            .consume<any>(SocketEvents.multipleCamerasDeviceInfoNotification)
            .pipe(takeUntil(this.destroy$))
            .subscribe((response) => {
                const notificationSessionToken = response?.sessionToken;
                if (this.manualDiscoverySessionToken && notificationSessionToken !== this.manualDiscoverySessionToken) {
                    return;
                }

                const details = response?.details;
                if (!details) return;

                if (typeof details.progress === 'number') {
                    this.manualDiscoveryProgress = details.progress;
                }

                const batch = Array.isArray(details.batch) ? details.batch : [];
                if (!batch.length) return;

                // Merge by ipAddress
                const byIp = new Map<string, any>();
                (this.manualDiscoveryRows || []).forEach((row: any) => byIp.set(row.ipAddress, row));

                batch.forEach((item: any) => {
                    const existing = byIp.get(item.ipAddress) || {};
                    const ping = item.pingMs || {};
                    byIp.set(item.ipAddress, {
                        ...existing,
                        ipAddress: item.ipAddress,
                        macAddress: item.macAddress || existing.macAddress,
                        cameraBrand: item.cameraBrand || existing.cameraBrand,
                        name: item.name || existing.name,
                        pingStats: {
                            min: ping.min,
                            max: ping.max,
                            avg: ping.avg,
                            std: ping.mdev, // map mdev -> std column
                            state: ping.state,
                        }
                    });

                    // Also enrich the camera object we keep for later auth (brand/mac if available)
                    const camState = this.cameraStatesMap.get(item.ipAddress);
                    if (camState) {
                        const updatedCam = {
                            ...camState.camera,
                            macAddress: item.macAddress || camState.camera.macAddress,
                            cameraBrand: item.cameraBrand || camState.camera.cameraBrand,
                        } as any;
                        this.cameraStatesMap.set(item.ipAddress, { ...camState, camera: updatedCam });
                    }
                });

                this.manualDiscoveryRows = Array.from(byIp.values());
                if (!this.csvImportRows?.length) {
                    this.applyDefaultBrandPathsToCredentials();
                }
            });
    }

    private applyDefaultBrandPathsToCredentials(): void {
        if (this.isOnvifFlow) {
            return;
        }
        if (this.manualDiscoveryEnabled && this.manualDeviceType === 'nvr') {
            return;
        }

        const commonBrand = this.getCommonSupportedBrand(this.getCamerasForBrandCheck());
        if (!commonBrand) {
            return;
        }

        const paths = AddCameraWizardModels.BRAND_DEFAULT_STREAM_PATHS[commonBrand];
        const firstUrl = this.credentialsArr[0]?.urls?.[0];
        if (!paths || !firstUrl) {
            return;
        }

        if (!firstUrl.mainUrl) {
            firstUrl.mainUrl = paths.mainUrl;
        }
        if (!firstUrl.subUrl) {
            firstUrl.subUrl = paths.subUrl;
        }
    }

    private getCamerasForBrandCheck(): Camera[] {
        if (this.cameraStatesMap.size > 0) {
            return Array.from(this.cameraStatesMap.values()).map((state) => state.camera);
        }
        return this.selectedCameras ?? [];
    }

    private getCommonSupportedBrand(cameras: Camera[]): AddCameraWizardModels.SupportedCameraBrand | null {
        if (!cameras.length) {
            return null;
        }

        const brands = cameras.map((camera) => this.resolveSupportedBrand(camera.cameraBrand as string));
        const firstBrand = brands[0];
        if (firstBrand === null) {
            return null;
        }
        if (!brands.every((brand) => brand === firstBrand)) {
            return null;
        }
        return firstBrand;
    }

    private resolveSupportedBrand(brand: string | undefined): AddCameraWizardModels.SupportedCameraBrand | null {
        const normalized = (brand ?? '').trim().toLowerCase();
        switch (normalized) {
            case AddCameraWizardModels.SupportedCameraBrand.Hikvision:
                return AddCameraWizardModels.SupportedCameraBrand.Hikvision;
            case AddCameraWizardModels.SupportedCameraBrand.Dahua:
                return AddCameraWizardModels.SupportedCameraBrand.Dahua;
            case AddCameraWizardModels.SupportedCameraBrand.Uniview:
                return AddCameraWizardModels.SupportedCameraBrand.Uniview;
            case AddCameraWizardModels.SupportedCameraBrand.Provision:
                return AddCameraWizardModels.SupportedCameraBrand.Provision;
            default:
                return null;
        }
    }

    // Camera selection methods
    public isCameraSelected(cameraId: string): boolean {
        return this.selectedCameraIds.has(cameraId);
    }

    public isCameraSelectionDisabledBySlots(cameraId: string): boolean {
        return !this.isCameraSelected(cameraId)
            && this.selectedCount >= this.maxSelectableCameras
            && this.maxSelectableCameras !== -1;
    }

    public toggleCameraSelection(cameraId: string): void {
        if (this.selectedCameraIds.has(cameraId)) {
            this.selectedCameraIds.delete(cameraId);
        } else {
            // Only enforce limit if maxSelectableCameras is not -1 (no limit)
            if (this.maxSelectableCameras !== -1 && this.selectedCameraIds.size >= this.maxSelectableCameras) {
                return;
            }
            this.selectedCameraIds.add(cameraId);
        }
        this.emitSelectedCameras();
    }

    public toggleAllCameras(): void {
        const successfulCameras = this.getOrderedSuccessfulStates();
        // If no limit (-1), select all; otherwise limit to maxSelectableCameras
        const selectable = this.maxSelectableCameras === -1 
            ? successfulCameras 
            : successfulCameras.slice(0, this.maxSelectableCameras);
        const allSelected = selectable.length > 0 && selectable.every(s => this.selectedCameraIds.has(s.camera.id));

        if (allSelected) {
            // Deselect all
            selectable.forEach(s => this.selectedCameraIds.delete(s.camera.id));
        } else {
            // Select all successful (or first N if limit is enabled)
            this.selectedCameraIds.clear();
            selectable.forEach(s => this.selectedCameraIds.add(s.camera.id));
        }
        this.emitSelectedCameras();
    }

    public get areAllSuccessfulSelected(): boolean {
        const successful = this.getOrderedSuccessfulStates();
        // If no limit (-1), check all; otherwise check up to maxSelectableCameras
        const selectable = this.maxSelectableCameras === -1 
            ? successful 
            : successful.slice(0, this.maxSelectableCameras);
        return selectable.length > 0 && selectable.every(s => this.selectedCameraIds.has(s.camera.id));
    }

    public get selectedCount(): number {
        return this.selectedCameraIds.size;
    }

    private emitSelectedCameras(): void {
        // Emit updated state with selection info
        this.cameraDetailsReceived.emit(this.cameraStatesMap);
        this.selectedCamerasChanged.emit(this.selectedCameraIds);
    }

    private getOrderedSuccessfulStates(): CameraAuthState[] {
        const successful = this.cameraStatesArray.filter(s => s.status === 'success');

        // NVR: order by channel; otherwise by IP then name for deterministic "select all"
        if (this.manualDiscoveryEnabled && this.manualDeviceType === 'nvr') {
            return successful.sort((a, b) => (this.getChannel(a) ?? 0) - (this.getChannel(b) ?? 0));
        }

        const ipToNum = (ip?: string) => {
            if (!ip) return 0;
            const parts = ip.split('.').map(p => parseInt(p, 10));
            if (parts.length !== 4 || parts.some(n => Number.isNaN(n))) return 0;
            return (parts[0] << 24) + (parts[1] << 16) + (parts[2] << 8) + parts[3];
        };

        return successful.sort((a, b) => {
            const ipA = ipToNum(a.camera?.ipAddress);
            const ipB = ipToNum(b.camera?.ipAddress);
            if (ipA !== ipB) return ipA - ipB;
            return (a.camera?.name || '').localeCompare(b.camera?.name || '');
        });
    }

    private enforceSelectionLimit(): void {
        // Skip enforcement if no limit (-1)
        if (this.maxSelectableCameras === -1) return;
        if (this.selectedCameraIds.size <= this.maxSelectableCameras) return;

        const successful = this.getOrderedSuccessfulStates();
        const allowed = new Set(successful.slice(0, this.maxSelectableCameras).map(s => s.camera.id));
        this.selectedCameraIds = new Set(Array.from(this.selectedCameraIds).filter(id => allowed.has(id)).slice(0, this.maxSelectableCameras));
        this.emitSelectedCameras();
    }

    // Credentials management
    public addCredentialRow(): void {
        this.credentialsArr.push({
            username: '',
            password: '',
            port: 554,
            protocol: LocationModel.RtspProtocol.UDP,
            urls: [{ mainUrl: '', subUrl: '' }],
            showPassword: false,
            nvrVendor: 'hikvision',
            subStream: true,
            channelCount: 4,
        });
    }

    public removeCredentialRow(index: number): void {
        if (this.credentialsArr.length > 1) {
            this.credentialsArr.splice(index, 1);
        }
    }

    public addUrlRow(credentialIndex: number): void {
        this.credentialsArr[credentialIndex].urls.push({ mainUrl: '', subUrl: '' });
    }

    public removeUrlRow(credentialIndex: number, urlIndex: number): void {
        if (this.credentialsArr[credentialIndex].urls.length > 1) {
            this.credentialsArr[credentialIndex].urls.splice(urlIndex, 1);
        }
    }

    // Toggle credentials visibility
    public toggleCredentials(): void {
        this.showCredentials = !this.showCredentials;
    }

    // Connect button handler
    public onConnect(): void {
        this.isConnecting = true;
        this.showCredentials = false;
        this.progress = 0;
        this.currentSessionToken = null; // Reset session token
        this.nvrErrorBanner = null;

        const isNvr = this.manualDiscoveryEnabled && this.manualDeviceType === 'nvr';
        const isOnvif = this.isOnvifFlow;

        if (!this.credentialsArr[0]?.urls[0]?.mainUrl) {
            this.credentialsArr[0].urls[0].mainUrl = '/';
        }

        // NVR flow: build channel cameras from brand presets (id = uuid), and request will return those ids.
        if (isNvr) {
            const nvrIp = this.selectedCameras?.[0]?.ipAddress;
            if (!nvrIp) {
                this.isConnecting = false;
                this.nvrErrorBanner = this.translateService.instant('ADD_CAMERA_WIZARD.AUTH.ERRORS.NVR_ENTER_IP');
                this.showCredentials = true;
                return;
            }

            const genetecCred = (this.credentialsArr || []).find((c) => c?.nvrVendor === WizardAuthenticationComponent.GENETEC_VENDOR);
            if (genetecCred) {
                this.connectGenetec(nvrIp, genetecCred);
                return;
            }

            // Reset camera map to represent channels (per credential urls)
            this.cameraStatesMap.clear();
            this.selectedCameraIds.clear();

            this.credentialsArr = (this.credentialsArr || []).map((cred: any) => {
                const nvrVendor = (cred?.nvrVendor || 'hikvision') as string;
                const channelCount = Number(cred?.channelCount || 4);
                const subStream = cred?.subStream ?? true;

                const urls = this.getNvrRtspChannelStreams(nvrVendor, channelCount).map((s) => ({
                    id: uuid.v4(),
                    channel: s.channel,
                    mainUrl: s.mainPath,
                    subUrl: subStream ? s.subPath : '',
                }));

                return { ...cred, nvrVendor, channelCount, subStream, urls };
            });

            // Create placeholder camera states for each channel url id
            this.credentialsArr.forEach((cred: any, credIdx: number) => {
                (cred.urls || []).forEach((u: any, idx: number) => {
                    const id = u.id;
                    const camera: Camera = {
                        id,
                        ipAddress: nvrIp,
                        name: this.translateService.instant('ADD_CAMERA_WIZARD.AUTH.NVR_CHANNEL_CAMERA_NAME', { channel: u.channel || (idx + 1) }),
                        cameraBrand: 'Unknown' as any,
                    };
                    // Keep channel number for UI table
                    (camera as any).channel = u.channel || (idx + 1);
                    this.cameraStatesMap.set(id, { camera, status: 'loading' });
                });
            });

            // Subscribe to socket updates first
            this.subscribeToDetailsUpdates();

            const request: LocationModel.GetMultipleCameraDetailsRequest = {
                locationId: this.locationId,
                edgeId: this.edgeId,
                create: false,
                data: {
                    nvr: true,
                    isOnvif: false,
                    cameraInfoArr: [{ ip: nvrIp, cameraBrand: 'Unknown' } as any], // no id for NVR
                    credentialsArr: this.credentialsArr as any,
                    snapshotWidth: 640,
                    snapshotHeight: 360
                }
            };

            this.edgeService.getMultipleCameraDetails(request).subscribe({
                next: (response: any) => {
                    if (response?.messageId) {
                        this.currentSessionToken = response.messageId;
                    } else if (response?.token?.session) {
                        this.currentSessionToken = response.token.session;
                    }
                },
                error: (error) => {
                    console.error('Failed to get multiple camera details (NVR):', error);
                    this.isConnecting = false;
                    this.showCredentials = true;
                    this.nvrErrorBanner = error?.error?.message || this.translateService.instant('ADD_CAMERA_WIZARD.AUTH.ERRORS.NVR_START_FAILED');
                }
            });

            return;
        }

        // Non-NVR flow (existing)
        const camerasToAuth: Camera[] = [];
        this.cameraStatesMap.forEach((state, id) => {
            if (state.status !== 'success') {
                this.cameraStatesMap.set(id, { ...state, status: 'loading' });
                camerasToAuth.push(state.camera);
            }
        });

        if (camerasToAuth.length === 0) {
            // All cameras are already successful
            this.isConnecting = false;
            this.isConnected = true;
            this.progress = 100;
            return;
        }

        // Subscribe to socket updates first
        this.subscribeToDetailsUpdates();

        // Build request - only for cameras that need auth
        const cameraInfoArr = this.adaptCamerasToCameraInfo(camerasToAuth);
        const credentialsArrToSend = isOnvif
            ? (this.credentialsArr || []).map((c: any) => ({
                username: c?.username,
                password: c?.password,
                subStream: c?.subStream ?? true,
            }))
            : this.credentialsArr;

        const request: LocationModel.GetMultipleCameraDetailsRequest = {
            locationId: this.locationId,
            edgeId: this.edgeId,
            create: false,
            data: {
                cameraInfoArr,
                credentialsArr: credentialsArrToSend as any,
                snapshotWidth: 640,
                snapshotHeight: 360,
                isOnvif: isOnvif,
            }
        };

        // Call API
        this.edgeService.getMultipleCameraDetails(request).subscribe({
            next: (response: any) => {
                console.log('Get multiple camera details started:', response);
                // Store the session token (messageId) from the response
                if (response?.messageId) {
                    this.currentSessionToken = response.messageId;
                } else if (response?.token?.session) {
                    this.currentSessionToken = response.token.session;
                }
            },
            error: (error) => {
                console.error('Failed to get multiple camera details:', error);
                this.isConnecting = false;
                // Set only loading cameras to error
                this.cameraStatesMap.forEach((state, id) => {
                    if (state.status === 'loading') {
                        this.cameraStatesMap.set(id, {
                            ...state,
                            status: 'error',
                            errorMessage: this.translateService.instant('ADD_CAMERA_WIZARD.AUTH.ERRORS.INIT_CONNECTION_FAILED')
                        });
                    }
                });
            }
        });
    }

    public onNvrBrandChange(credential: LocationModel.Credentials): void {
        if (credential?.nvrVendor === WizardAuthenticationComponent.GENETEC_VENDOR) {
            credential.channelCount = undefined;
            credential.protocol = credential.protocol ?? LocationModel.RtspProtocol.SECURED;
            this.applyGenetecDefaults(credential);
        } else if (credential && credential.channelCount == null) {
            credential.channelCount = 4;
        }
    }

    private loadGenetecConfig(): void {
        this.integrationsService.getGenetecIntegration()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (config) => {
                    this.genetecConfig = config;
                    this.credentialsArr
                        .filter(c => c?.nvrVendor === WizardAuthenticationComponent.GENETEC_VENDOR)
                        .forEach(c => this.applyGenetecDefaults(c));
                    this.changeDetectorRef.detectChanges();
                },
                error: () => {
                    this.genetecConfig = null;
                },
            });
    }

    private applyGenetecDefaults(credential: LocationModel.Credentials): void {
        const webSdk = this.genetecConfig?.webSdk;
        const mediaGateway = this.genetecConfig?.mediaGateway;

        if (!webSdk) {
            return;
        }

        credential.genetecWebSdk = {
            ip: credential.genetecWebSdk?.ip || webSdk.ip,
            port: credential.genetecWebSdk?.port || webSdk.port,
            username: credential.genetecWebSdk?.username || webSdk.username,
            password: credential.genetecWebSdk?.password || '',
            baseUri: credential.genetecWebSdk?.baseUri || webSdk.baseUri,
        };

        const stream = mediaGateway ?? webSdk;
        credential.username = credential.username || stream.username;
        credential.port = credential.port && credential.port !== 554 ? credential.port : stream.port;
        credential.genetecServerIp = credential.genetecServerIp || stream.ip;
    }

    public get genetecDefaultsHint(): string | null {
        if (!this.genetecConfig?.webSdk) {
            return null;
        }
        return this.genetecConfig.mediaGateway
            ? this.translateService.instant('ADD_CAMERA_WIZARD.AUTH.GENETEC_DEFAULTS_GATEWAY')
            : this.translateService.instant('ADD_CAMERA_WIZARD.AUTH.GENETEC_DEFAULTS_WEB_SDK');
    }

    private connectGenetec(nvrIp: string, credential: LocationModel.Credentials): void {
        this.cameraStatesMap.clear();
        this.selectedCameraIds.clear();

        const webSdk = credential.genetecWebSdk;
        const streamDefaults = this.genetecConfig?.mediaGateway ?? this.genetecConfig?.webSdk;

        const username = credential?.username || streamDefaults?.username || '';
        const password = credential?.password || '';

        credential.genetecServerIp = nvrIp || streamDefaults?.ip || credential.genetecServerIp;

        this.integrationsService.getGenetecCameras({
            edgeId: this.edgeId,
            ip: webSdk?.ip || nvrIp,
            port: webSdk?.port,
            baseUri: webSdk?.baseUri,
            username: webSdk?.username || username,
            password: webSdk?.password || password,
        }).subscribe({
            next: (response: IntegrationsModel.GenetecCamerasResponse) => {
                const onlineCameras = (response?.cameras || []).filter((c) => c.isOnline);

                if (onlineCameras.length === 0) {
                    this.isConnecting = false;
                    this.isConnected = true;
                    this.progress = 100;
                    this.showCredentials = true;
                    this.nvrErrorBanner = this.translateService.instant('ADD_CAMERA_WIZARD.AUTH.ERRORS.GENETEC_NO_CAMERAS');
                    this.emitSelectedCameras();
                    return;
                }

                const useSubStream = credential.subStream ?? false;
                const urls = onlineCameras.map((genetecCamera, index) => ({
                    id: uuid.v4(),
                    channel: index + 1,
                    mainUrl: WizardAuthenticationComponent.buildGenetecStreamPath(genetecCamera.genetecGuid),
                    subUrl: useSubStream ? WizardAuthenticationComponent.buildGenetecSubStreamPath(genetecCamera.genetecGuid) : '',
                }));

                credential.port = credential.port
                    || streamDefaults?.port
                    || WizardAuthenticationComponent.GENETEC_MEDIA_GATEWAY_PORT;
                credential.protocol = credential.protocol ?? LocationModel.RtspProtocol.SECURED;
                credential.channelCount = urls.length;
                credential.urls = urls;

                onlineCameras.forEach((genetecCamera, index) => {
                    const id = urls[index].id;
                    const camera: Camera = {
                        id,
                        cameraId: id,
                        ipAddress: genetecCamera.ip,
                        name: genetecCamera.name,
                        username,
                        password,
                        cameraBrand: CameraBrands.Unknown,
                        genetecGuid: genetecCamera.genetecGuid,
                        isOnline: genetecCamera.isOnline,
                    };
                    this.cameraStatesMap.set(id, { camera, status: 'loading' });
                });
                this.emitSelectedCameras();

                this.subscribeToDetailsUpdates();

                const request: LocationModel.GetMultipleCameraDetailsRequest = {
                    locationId: this.locationId,
                    edgeId: this.edgeId,
                    create: false,
                    data: {
                        nvr: true,
                        isOnvif: false,
                        cameraInfoArr: [{ ip: nvrIp, id: nvrIp, cameraBrand: 'Unknown' } as any],
                        credentialsArr: [credential] as any,
                        snapshotWidth: 640,
                        snapshotHeight: 360,
                    },
                };

                this.edgeService.getMultipleCameraDetails(request).subscribe({
                    next: (resp: any) => {
                        this.currentSessionToken = resp?.messageId ?? resp?.token?.session ?? this.currentSessionToken;
                    },
                    error: (err) => {
                        console.error('Failed to authenticate Genetec cameras:', err);
                        this.isConnecting = false;
                        this.showCredentials = true;
                        this.nvrErrorBanner = err?.error?.message || this.translateService.instant('ADD_CAMERA_WIZARD.AUTH.ERRORS.NVR_START_FAILED');
                    },
                });
            },
            error: (error) => {
                console.error('Failed to discover Genetec cameras:', error);
                this.isConnecting = false;
                this.showCredentials = true;
                this.nvrErrorBanner = error?.error?.message || this.translateService.instant('ADD_CAMERA_WIZARD.AUTH.ERRORS.GENETEC_DISCOVERY_FAILED');
            }
        });
    }

    // Adapter: Convert Camera[] to CameraInfo[]
    private adaptCamerasToCameraInfo(cameras: Camera[]): LocationModel.CameraInfo[] {
        return cameras.map(camera => ({
            ip: camera.ipAddress!,
            id: camera.id,
            cameraBrand: (camera.cameraBrand as any) || 'Unknown',
            onvifData: camera.onvifData
        } as any));
    }

    private getNvrRtspChannelStreams(
        vendor: string,
        channelCount: number
    ): Array<{ channel: number; mainPath: string; subPath: string }> {
        if (!Number.isInteger(channelCount) || channelCount <= 0) {
            throw new Error(`channelCount must be a positive integer (got ${channelCount})`);
        }

        const mkPaths = (channel: number): { mainPath: string; subPath: string } => {
            switch (vendor) {
                case 'hikvision':
                    return {
                        mainPath: `/Streaming/channels/${channel}01`,
                        subPath: `/Streaming/channels/${channel}02`,
                    };
                case 'uniview':
                    return {
                        mainPath: `/unicast/c${channel}/s0/live`,
                        subPath: `/unicast/c${channel}/s1/live`,
                    };
                case 'dahua':
                    return {
                        mainPath: `/cam/realmonitor?channel=${channel}&subtype=0`,
                        subPath: `/cam/realmonitor?channel=${channel}&subtype=1`,
                    };
                case 'provision':
                    return {
                        mainPath: `/chID=${channel}&streamType=main&linkType=tcp`,
                        subPath: `/chID=${channel}&streamType=sub&linkType=tcp`,
                    };
                default:
                    return {
                        mainPath: `/cam/realmonitor?channel=${channel}&subtype=0`,
                        subPath: `/cam/realmonitor?channel=${channel}&subtype=1`,
                    };
            }
        };

        const streams: Array<{ channel: number; mainPath: string; subPath: string }> = [];
        for (let ch = 1; ch <= channelCount; ch++) {
            const { mainPath, subPath } = mkPaths(ch);
            streams.push({ channel: ch, mainPath, subPath });
        }
        return streams;
    }

    // Subscribe to socket updates
    private subscribeToDetailsUpdates(): void {
        this.socketMainService
            .consume<any>(SocketEvents.multipleCamerasDetailsNotification)
            .pipe(takeUntil(this.destroy$))
            .subscribe((response) => {
                console.log('Multiple cameras details notification:', response);

                // Filter by sessionToken - only process notifications from current auth request
                const notificationSessionToken = response?.sessionToken;
                if (this.currentSessionToken && notificationSessionToken !== this.currentSessionToken) {
                    console.log(`Ignoring auth notification from different session: ${notificationSessionToken} (current: ${this.currentSessionToken})`);
                    return;
                }

                if (!response?.details) {
                    return;
                }

                try {
                    const result: LocationModel.CamerasDetailsResult = response.details;

                    // Update camera states from details FIRST (before progress handling)
                    if (result.details && Array.isArray(result.details)) {
                        result.details.forEach((detail: LocationModel.CameraConnectionDetails) => {
                            const existingState = this.cameraStatesMap.get(detail.id);
                            if (existingState) {
                                const isSuccess = detail.mainStreamConnectionData?.connectionResponseCode === 200;
                                this.cameraStatesMap.set(detail.id, {
                                    ...existingState,
                                    connectionDetails: detail,
                                    status: isSuccess ? 'success' : 'error',
                                    errorMessage: !isSuccess ? detail.mainStreamConnectionData?.msg : undefined
                                });
                            }
                        });
                    }

                    // Update progress
                    if (result.progress !== undefined) {
                        this.progress = result.progress;

                        if (this.progress >= 100) {
                            this.isConnecting = false;
                            this.isConnected = true;

                            // Manual flow: user already selected IPs, so auto-select all successful cameras
                            if (this.manualDiscoveryEnabled) {
                                this.selectedCameraIds.clear();
                                const allSuccessful = this.getOrderedSuccessfulStates();
                                // If no limit (-1), select all; otherwise limit to maxSelectableCameras
                                const ordered = this.maxSelectableCameras === -1 
                                    ? allSuccessful 
                                    : allSuccessful.slice(0, this.maxSelectableCameras);
                                ordered.forEach(s => this.selectedCameraIds.add(s.camera.id));
                                this.emitSelectedCameras();
                            }

                            // NVR: show banner if none succeeded
                            if (this.manualDiscoveryEnabled && this.manualDeviceType === 'nvr') {
                                const successCount = this.cameraStatesArray.length;
                                if (successCount === 0) {
                                    this.nvrErrorBanner = this.translateService.instant('ADD_CAMERA_WIZARD.AUTH.ERRORS.NVR_NO_CREDENTIALS_PASSED');
                                    this.showCredentials = true;
                                }
                            }
                        }
                    }

                    // Emit updated state
                    this.cameraDetailsReceived.emit(this.cameraStatesMap);
                } catch (error) {
                    console.error('Failed to parse camera details response:', error);
                }
            });
    }

    public onBack(): void {
        this.back.emit();
    }

    public get isNextDisabled(): boolean {
        // Disable if still connecting or no successful cameras
        if (this.isConnecting) return true;

        let hasSuccess = false;
        this.cameraStatesMap.forEach(state => {
            if (state.status === 'success') hasSuccess = true;
        });

        return !hasSuccess;
    }

    // Get camera state array for template iteration
    public get cameraStatesArray(): CameraAuthState[] {
        const all = Array.from(this.cameraStatesMap.values());

        // NVR flow: show only success items when finished
        const visible = (this.manualDiscoveryEnabled && this.manualDeviceType === 'nvr' && this.isConnected)
            ? all.filter(s => s.status === 'success')
            : all;

        return this.sortCameraStatesForDisplay(visible);
    }

    private sortCameraStatesForDisplay(states: CameraAuthState[]): CameraAuthState[] {
        const statusRank: Record<CameraAuthState['status'], number> = {
            success: 0,
            loading: 1,
            pending: 1,
            error: 2,
        };

        const ipToNum = (ip?: string) => {
            if (!ip) return 0;
            const parts = ip.split('.').map(p => parseInt(p, 10));
            if (parts.length !== 4 || parts.some(n => Number.isNaN(n))) return 0;
            return (parts[0] << 24) + (parts[1] << 16) + (parts[2] << 8) + parts[3];
        };

        const isNvr = this.manualDiscoveryEnabled && this.manualDeviceType === 'nvr';

        return [...(states || [])].sort((a, b) => {
            const rankA = statusRank[a.status] ?? 99;
            const rankB = statusRank[b.status] ?? 99;
            if (rankA !== rankB) return rankA - rankB;

            // Within same status keep deterministic order
            if (isNvr) {
                const chA = this.getChannel(a) ?? 0;
                const chB = this.getChannel(b) ?? 0;
                if (chA !== chB) return chA - chB;
            }

            const ipA = ipToNum(a.camera?.ipAddress);
            const ipB = ipToNum(b.camera?.ipAddress);
            if (ipA !== ipB) return ipA - ipB;

            const nameA = (a.camera?.name || '').toLowerCase();
            const nameB = (b.camera?.name || '').toLowerCase();
            if (nameA !== nameB) return nameA.localeCompare(nameB);

            return (a.camera?.id || '').localeCompare(b.camera?.id || '');
        });
    }

    public getChannel(state: CameraAuthState): number | undefined {
        const direct = (state?.camera as any)?.channel;
        if (typeof direct === 'number') return direct;
        const id = state?.camera?.id;
        if (!id) return undefined;

        for (const cred of (this.credentialsArr as any[]) || []) {
            for (const u of (cred?.urls as any[]) || []) {
                if (u?.id === id) {
                    const ch = Number(u?.channel);
                    return Number.isFinite(ch) ? ch : undefined;
                }
            }
        }
        return undefined;
    }

    /**
     * Normalizes edge snapshot paths that may already be absolute CDN / proxy URLs
     * so private-storage base resolution can still be applied.
     */
    private normalizeSnapshotPath(snapshotPath: string): string {
        if (!snapshotPath.startsWith('http')) {
            return snapshotPath.replace(/^\/+/, '');
        }

        const markers = [
            '/storage-download/snapshots/',
            '/lumanaai-snapshots/',
            `${api.thumbnails.gcpSnapshotsUrl}/`,
            `${api.thumbnails.awsSnapshotsUrl}/`,
        ];
        for (const marker of markers) {
            const idx = snapshotPath.indexOf(marker);
            if (idx !== -1) {
                return snapshotPath.slice(idx + marker.length).replace(/^\/+/, '');
            }
        }
        return snapshotPath;
    }

    // Build full snapshot URL from relative path (private storage when configured for this edge/location).
    public getSnapshotUrl(snapshotPath: string | undefined): string {
        if (!snapshotPath) return '';
        const relativePath = this.normalizeSnapshotPath(snapshotPath);
        if (relativePath.startsWith('http')) {
            return relativePath;
        }
        return `${this.thumbnailsService.getBucketBaseUrl(
            StorageBucket.Snapshots,
            true,
            false,
            this.edgeId,
            this.locationId,
        )}/${relativePath}`;
    }

    /** Cloud/proxy fallback when the private MinIO URL fails to load. */
    public getSnapshotFallbackUrl(snapshotPath: string | undefined): string {
        if (!snapshotPath) return '';
        const relativePath = this.normalizeSnapshotPath(snapshotPath);
        if (relativePath.startsWith('http')) {
            return relativePath;
        }
        // Omit edgeId/locationId so private storage is not used.
        return `${this.thumbnailsService.getBucketBaseUrl(StorageBucket.Snapshots)}/${relativePath}`;
    }

    private snapshotKey(state: CameraAuthState, stream: 'main' | 'sub' = 'main'): string {
        return `${state?.camera?.id || 'unknown'}:${stream}`;
    }

    public getSnapshotSrc(state: CameraAuthState, stream: 'main' | 'sub' = 'main'): string | null {
        const path =
            stream === 'main'
                ? state?.connectionDetails?.mainStreamConnectionData?.snapshotUrl
                : state?.connectionDetails?.subStreamConnectionData?.snapshotUrl;

        if (!path) return null;

        const key = this.snapshotKey(state, stream);
        const attempt = this.snapshotAttemptByKey.get(key) ?? 0;
        if (attempt >= this.snapshotMaxAttempts) return null;

        const base = this.getSnapshotUrl(path);
        const bust = this.snapshotBustByKey.get(key);
        if (!bust) return base;
        const joiner = base.includes('?') ? '&' : '?';
        return `${base}${joiner}v=${bust}`;
    }

    public getSnapshotFallbackSrc(state: CameraAuthState, stream: 'main' | 'sub' = 'main'): string {
        const path =
            stream === 'main'
                ? state?.connectionDetails?.mainStreamConnectionData?.snapshotUrl
                : state?.connectionDetails?.subStreamConnectionData?.snapshotUrl;
        return this.getSnapshotFallbackUrl(path);
    }

    public onSnapshotLoad(state: CameraAuthState, stream: 'main' | 'sub' = 'main'): void {
        const key = this.snapshotKey(state, stream);
        this.snapshotAttemptByKey.delete(key);
        this.snapshotBustByKey.delete(key);
        const timer = this.snapshotTimerByKey.get(key);
        if (timer) {
            clearTimeout(timer);
            this.snapshotTimerByKey.delete(key);
        }
    }

    public onSnapshotError(state: CameraAuthState, stream: 'main' | 'sub' = 'main'): void {
        const key = this.snapshotKey(state, stream);
        const currentAttempt = this.snapshotAttemptByKey.get(key) ?? 0;

        // Already scheduled or exhausted
        if (currentAttempt >= this.snapshotMaxAttempts) return;
        if (this.snapshotTimerByKey.has(key)) return;

        const nextAttempt = currentAttempt + 1;
        this.snapshotAttemptByKey.set(key, nextAttempt);

        if (nextAttempt >= this.snapshotMaxAttempts) {
            // Give up: template will fall back to placeholder once src becomes null
            return;
        }

        const timer = setTimeout(() => {
            // cache-bust to force retry
            this.snapshotBustByKey.set(key, Date.now());
            this.snapshotTimerByKey.delete(key);
        }, this.snapshotRetryDelayMs);

        this.snapshotTimerByKey.set(key, timer);
    }

    // Get error code from camera state
    public getErrorCode(state: CameraAuthState): number | undefined {
        return state.connectionDetails?.mainStreamConnectionData?.connectionResponseCode;
    }

    // Handle add credentials/rtsp path action - shows credentials and scrolls to top
    public onAddCredentials(): void {
        this.showCredentials = true;
        // Scroll to top of the credentials section
        setTimeout(() => {
            const credentialsSection = document.querySelector('.credentials-section');
            if (credentialsSection) {
                credentialsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 100);
    }

    public getSnapshot(): WizardAuthenticationSnapshot {
        // Clone credentials (plain objects)
        const credentialsArr = (this.credentialsArr || []).map((c: any) => ({
            ...c,
            urls: (c?.urls || []).map((u: any) => ({ ...u })),
        }));

        const cameraStatesMap: Array<[string, CameraAuthState]> = Array.from(this.cameraStatesMap.entries()).map(
            ([id, s]) => [
                id,
                {
                    ...s,
                    camera: { ...(s.camera as any) },
                    connectionDetails: s.connectionDetails ? ({ ...(s.connectionDetails as any) } as any) : undefined,
                },
            ]
        );

        return {
            isConnecting: this.isConnecting,
            isConnected: this.isConnected,
            progress: this.progress,
            showCredentials: this.showCredentials,
            nvrErrorBanner: this.nvrErrorBanner,
            credentialsArr,
            cameraStatesMap,
            selectedCameraIds: Array.from(this.selectedCameraIds),
            manualDiscoveryProgress: this.manualDiscoveryProgress,
            manualDiscoveryRows: Array.isArray(this.manualDiscoveryRows) ? [...this.manualDiscoveryRows] : [],
        };
    }

    public restoreSnapshot(snapshot: WizardAuthenticationSnapshot): void {
        this.isConnecting = !!snapshot?.isConnecting;
        this.isConnected = !!snapshot?.isConnected;
        this.progress = Number(snapshot?.progress ?? 0);
        this.showCredentials = snapshot?.showCredentials ?? true;
        this.nvrErrorBanner = snapshot?.nvrErrorBanner ?? null;

        this.credentialsArr = (snapshot?.credentialsArr || []).map((c: any) => ({
            ...c,
            urls: (c?.urls || []).map((u: any) => ({ ...u })),
        })) as any;

        this.cameraStatesMap = new Map<string, CameraAuthState>(snapshot?.cameraStatesMap || []);
        this.selectedCameraIds = new Set<string>(snapshot?.selectedCameraIds || []);

        this.manualDiscoveryProgress = Number(snapshot?.manualDiscoveryProgress ?? 0);
        this.manualDiscoveryRows = Array.isArray(snapshot?.manualDiscoveryRows) ? [...snapshot.manualDiscoveryRows] : [];

        // Ensure selection obeys platform limit once it's known
        this.enforceSelectionLimit();

        // Emit to keep parent in sync (selected ids + state map)
        this.emitSelectedCameras();
    }

}
